import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { handleTimeoutPrompt } from "../../../store/handlePrompt";
import { logger } from "../../../helpers/logger";
import { initiateAXIOS } from "../../../config/axios";
import { contentCMSAPILimit } from "../../../api";
import { SET_ERROR_PROMPT_SCHEDULE_DIALOG } from "../../../store/actionTypes/scheduleActionType";
import { useDispatch } from "react-redux";
import { useDebounce } from "../../hooks";
import { IconButton, InputLabel, Stack, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import PreviewIcon from "@mui/icons-material/Preview";
import ContentDetails from "./detail/ContentDetails";
import { isEmpty } from "lodash";

const ContentTemplateSelectDropdown = ({
  selectContent,
  selectedContent,
  error,
  setError,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("cms");

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputSearch, setInputSearch] = useState("");

  const [openContentDetail, setOpenContentDetail] = useState(false);

  const handleOpenContentDetail = () => {
    setOpenContentDetail(true);
  };

  const handleCloseContentDetail = () => {
    setOpenContentDetail(false);
  };

  /**
   * 1 Second delay after input value change to do API fetch
   */
  const debouncedSearchTerm = useDebounce(inputSearch, 1000);

  const getCMSContent = async (search) => {
    const access_token = sessionStorage.getItem("access_token");
    setLoading(true);
    try {
      let query = search ? "&nameLike=" + search : "";
      const { data } = await initiateAXIOS.get(contentCMSAPILimit + query, {
        headers: { authorization: `Bearer ${access_token}` },
      });

      setOptions(data.items || []);
    } catch (error) {
      logger.error(error);

      handleTimeoutPrompt(dispatch, SET_ERROR_PROMPT_SCHEDULE_DIALOG);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      if (debouncedSearchTerm) {
        getCMSContent(debouncedSearchTerm);
      } else if (debouncedSearchTerm === "" && open) {
        getCMSContent();
      } else {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm, open] // Only call effect if debounced search term changes
  );

  return (
    <>
      <InputLabel>{t("contentSelector.inputLabel")}</InputLabel>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Autocomplete
          fullWidth
          size="small"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={options}
          getOptionLabel={(option) => option.name || ""}
          loading={loading}
          value={selectedContent}
          disabled={disabled}
          onInputChange={(_, value) => {
            setInputSearch(value);
          }}
          onChange={(_, newValue) => {
            selectContent(newValue);
            setError(false);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              helperText={error ? t("contentSelector.requiredText") : ""}
              placeholder={t("contentSelector.placeholder")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <>
          <Tooltip title={t("contentSelector.previewTooltip")} placement="top">
            <IconButton
              color="primary"
              disabled={isEmpty(selectedContent) || disabled}
              onClick={handleOpenContentDetail}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <ContentDetails
            open={openContentDetail}
            handleClose={handleCloseContentDetail}
            contentID={selectedContent?.id}
          />
        </>
      </Stack>
    </>
  );
};

export default ContentTemplateSelectDropdown;
